/*
  Meta Pixel - ecommerce events.

  This was ~178 lines of inline, parser-blocking JavaScript at the bottom of
  <head> (snippets/meta-pixel.liquid). The parser had to compile and execute it
  before it could start on <body>, and it re-serialised the entire cart object
  into every page's HTML.

  Now: the Liquid data lives in a #ark-meta-pixel-data JSON island, which the
  parser only tokenises, and the behaviour lives here, deferred and cacheable.

  Loaded from layout/theme.liquid AFTER pubsub.js. Deferred scripts execute in
  document order, so PUB_SUB_EVENTS (constants.js) and subscribe (pubsub.js) are
  guaranteed to exist by the time this runs.

  Fires: ViewContent (product pages), AddToCart (real adds only),
         InitiateCheckout (any checkout button). PageView stays in the inline
         base code, which must not be delayed.

  Purchase is deliberately absent: Shopify's checkout does not render the theme,
  so no theme code can run there. It belongs in Settings > Customer events.
*/
(function () {
  'use strict';

  var el = document.getElementById('ark-meta-pixel-data');
  if (!el) return;

  var DATA;
  try {
    DATA = JSON.parse(el.textContent);
  } catch (e) {
    return;
  }

  /* ================================================================
     CONFIG - the one thing you may need to change.

     catalogId() must return the SAME ids your Meta product catalog
     uses. If they do not match, events still arrive but Meta cannot
     tie them to products, so dynamic ads and catalog reporting stay
     empty.

     Default below = plain variant id. Correct for a manual pixel
     install and for catalogs built from a variant CSV/feed.

     If your catalog was created by the Facebook & Instagram sales
     channel, Shopify prefixes its ids - replace the return with:
         return 'shopify_PK_' + productId + '_' + variantId;
     ================================================================ */
  function catalogId(variantId, productId) {
    return String(variantId);
  }

  var CURRENCY = DATA.currency;

  /* Cart snapshot. A checkout click navigates away immediately, so we cannot
     await a /cart.js fetch at click time. Seeded from Liquid at render, then
     refreshed whenever the cart changes. */
  var cartState = DATA.cart;

  function pixelReady() {
    return typeof window.fbq === 'function';
  }

  /* Shopify money is in minor units (cents). */
  function money(minorUnits) {
    return Math.round(Number(minorUnits) || 0) / 100;
  }

  function lineToContent(line) {
    return {
      id: catalogId(line.variant_id || line.id, line.product_id),
      quantity: line.quantity || 1,
      item_price: money(line.final_price != null ? line.final_price : line.price)
    };
  }

  function refreshCart() {
    return fetch(DATA.cartUrl + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (response) { return response.json(); })
      .then(function (data) { if (data && data.items) cartState = data; })
      .catch(function () { /* keep the previous snapshot */ });
  }

  function cartPayload() {
    var items = (cartState && cartState.items) || [];
    return {
      content_type: 'product',
      content_ids: items.map(function (line) {
        return catalogId(line.variant_id || line.id, line.product_id);
      }),
      contents: items.map(lineToContent),
      num_items: items.reduce(function (total, line) { return total + (line.quantity || 0); }, 0),
      value: money(cartState && cartState.total_price),
      currency: CURRENCY
    };
  }

  /* ---------------------------------------------------------------
     InitiateCheckout

     Every checkout button in this theme carries name="checkout" - the
     cart drawer (snippets/cart-drawer.liquid), the /cart page
     (sections/main-cart-footer.liquid) and the cart notification
     popup (snippets/cart-notification.liquid). One delegated listener
     covers all three, and keeps working if the markup moves.

     Capture phase, so it runs before the theme's own submit handling.
     --------------------------------------------------------------- */
  document.addEventListener(
    'click',
    function (event) {
      var node = event.target;
      if (!node || typeof node.closest !== 'function') return;

      var button = node.closest('[name="checkout"]');
      if (!button || button.disabled || !pixelReady()) return;

      fbq('track', 'InitiateCheckout', cartPayload());
    },
    true
  );

  /* ---------------------------------------------------------------
     AddToCart

     Uses the theme's own pubsub (assets/pubsub.js, assets/constants.js).

     Only source 'product-form' is a genuine add - it is the response
     from /cart/add.js and contains just the added lines. This also
     covers the quick-add modal, which renders a product form.

     Excluded on purpose:
       'cart-items'       quantity edits and removals   (assets/cart.js)
       'quick-add'        /cart/update.js, returns the WHOLE cart
                          (assets/quick-add-bulk.js) - would over-report
       'external-refresh' background resync
                          (assets/standard-actions-override.js)
     --------------------------------------------------------------- */
  function wireCartEvents() {
    if (typeof window.subscribe !== 'function' || typeof window.PUB_SUB_EVENTS === 'undefined') {
      return false;
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (payload) {
      payload = payload || {};

      if (payload.source === 'product-form') {
        var added = payload.cartData || {};
        var lines = Array.isArray(added.items)
          ? added.items
          : (added.variant_id || added.id ? [added] : []);

        if (pixelReady() && lines.length) {
          fbq('track', 'AddToCart', {
            content_type: 'product',
            content_ids: lines.map(function (line) {
              return catalogId(line.variant_id || line.id, line.product_id);
            }),
            contents: lines.map(lineToContent),
            content_name: lines[0].product_title || lines[0].title || '',
            num_items: lines.reduce(function (total, line) { return total + (line.quantity || 1); }, 0),
            value: lines.reduce(function (total, line) {
              return total + money(line.final_line_price != null ? line.final_line_price : line.price);
            }, 0),
            currency: CURRENCY
          });
        }
      }

      refreshCart();
    });

    return true;
  }

  /* defer guarantees pubsub.js has already run; the DOMContentLoaded fallback
     only matters if this file is ever moved ahead of it. */
  if (!wireCartEvents()) {
    document.addEventListener('DOMContentLoaded', wireCartEvents, { once: true });
  }

  /* ---------------------------------------------------------------
     ViewContent - product pages only.
     --------------------------------------------------------------- */
  var vc = DATA.viewContent;
  if (vc && pixelReady()) {
    fbq('track', 'ViewContent', {
      content_type: 'product',
      content_ids: [catalogId(vc.variantId, vc.productId)],
      content_name: vc.title,
      content_category: vc.category,
      value: money(vc.priceMinor),
      currency: CURRENCY
    });
  }
})();
